namespace Solovis.Reports.Parameters.PropValueIncludes {

    export interface PropValuesToIncludeSetOptions {
        includeList?: PropValueToInclude[];
        showFromDate?: boolean;
    }

    export interface PropValueToInclude {
        /**
         * References the resource id source from the original data set (i.e. "entityResourceId", "investmentResourceId", etc.)
         * If value of this is "indirect", then the desire is to load this value off of the other property value.
         */
        resourceRef?: string;

        /**
         * Instead of a direct resource, if resourceRef is "indirect", this will indicate that another property value
         * should be pulled and it's property value should then be pulled.
         */
        indirectRef?: PropValueToInclude;

        /**
         * References the date data source which will be used as the date to pull the property value for.
         */
        dateRef?: string;

        /**
         * Property definition id of the property value to include.
         */
        propDefId?: number;

        /**
         * Exact date only implies that only a value for the exact end date should be included (otherwise, the latest value should be used).
         */
        exactDayOnly?: boolean;

        /**
         * Similar to the root level showFromDate, except this only applies to the PropValueToInclude it is set for.
         * If true, then a new Since Date column will be added to the output for this property value.
          */
        showFromDate?: boolean;
    }

    export interface PropValueToIncludeResourceOption {
        code: string;
        label: string;
        resourceTypeId?: number;

        /**
         * For a "manager"-code option only: the resourceRef this report already knows how to resolve
         * that Sys.Manager (an Entity-scoped property) should be looked up on, e.g. "entityOrHolding".
         * When set, selecting "Manager" is a shortcut that gets stored/sent as an indirect reference
         * through Sys.Manager, reusing this report's own resolution for that base resourceRef.
         * When absent, "manager" is left as a direct resourceRef (e.g. for a report whose line items
         * already are Managers, like Manager List, where no indirection is meaningful).
         */
        indirectRefCode?: string;
    }

    export interface PropValueToIncludeDateOption {
        code: string;
        label: string;
    }

    export class PropValuesToIncludeSetParameterControl extends Solovis.UI.BaseControl {

        private _portalServices: Solovis.Portal.IPortalServices;

        private _setOptions;
        private _resourceOptions: PropValueToIncludeResourceOption[];
        private _dateOptions: PropValueToIncludeDateOption[];

        private _setLabel;
        private _setEditLink;
        public static hasNewRender = true;

        // NOTE: Beta toggle for indirect property include support.
        static _supportIndirect = true;

        constructor(options) {
            super(options);

            this._portalServices = options.portalServices;

            this._setOptions = (typeof options.initialValue != "undefined") ? options.initialValue : null;
            this._dateOptions = options.settings ? options.settings.dateOptions : null;

            if (options.settings && options.settings.resourceOptions) {
                this._resourceOptions = options.settings.resourceOptions.slice(0);

                if (PropValuesToIncludeSetParameterControl._supportIndirect) {
                    this._resourceOptions.push({
                        code: "indirect",
                        label: "Another Property's Property Value"
                    });
                }
            }
        }

        public doRender() {
            if (this.options.renderMode === 'modern') {
                this.doRenderNew();
            } else {
                this.doRenderClassic();
            }
        }

        private async doRenderNew() {
            await Solovis.UX.Utils.placeComponent("ConfigureControl", this.container[0], { onClick: (event) => { this._showStatSetEditor() } })
        }

        private doRenderClassic() {
            let self = this;

            this._setLabel = $("<a>").text("Configure").appendTo(this.container);
            this._setLabel.click(function () {
                self._showStatSetEditor();
            });

            this._setEditLink = $("<a>").appendTo(this.container)
                .css("padding-left", "5px")
                .html("<span class='fa fa-pencil'></span>");
            this._setEditLink.click(function () {
                self._showStatSetEditor();
            });
        }


        private _showStatSetEditor() {
          let editorDialog = new PropValuesToIncludeSetEditorDialog({
                container: this.container[0],
                portalServices: this._portalServices,
                setOptions: this._setOptions,
                resourceOptions: this._resourceOptions,
                dateOptions: this._dateOptions,

                saveCallback: (newSetOptions) => {
                    this.setParameterValue(newSetOptions);
                }
            });

            editorDialog.show();
        }


        public _setCurrentStatSetOptions(newInteralSetOptions) {
            this._setOptions = newInteralSetOptions;
        }

        public setParameterValue(newValue, more?) {
            Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
                this, PropValuesToIncludeSetParameterControl, this._setCurrentStatSetOptions, newValue, more);
        }

        public getParameterValue() {
            return Solovis.deepCopy(this._setOptions);
        }

        public notifyParameterValueChanged() {
            $(this).trigger("parameterValueChanged");
        }



        public static getParamExtraInfo(
            reportControl: any,
            propValuesToIncludeParameterName: string,
            propValueToInclude: PropValueToInclude) {

            let extraInfo: any = {};
            let paramInfo;

            if (reportControl && reportControl._parameterSet && propValuesToIncludeParameterName) {
                paramInfo = reportControl._parameterSet.tryGetParameterInfoByName(propValuesToIncludeParameterName);
            }

            extraInfo.resourceRefLabel = propValueToInclude.resourceRef;
            extraInfo.dateRefLabel = propValueToInclude.dateRef;

            let codeToResourceOptionMap = {};
            let codeToDateOptionsMap = {};

            if (paramInfo && paramInfo.settings) {
                if (paramInfo.settings.resourceOptions) {
                    for (let resourceOption of paramInfo.settings.resourceOptions) {
                        codeToResourceOptionMap[resourceOption.code] = resourceOption;
                    }
                }
                if (paramInfo.settings.dateOptions) {
                    for (let dateOption of paramInfo.settings.dateOptions) {
                        codeToDateOptionsMap[dateOption.code] = dateOption;
                    }
                }
            }

            if (codeToResourceOptionMap[propValueToInclude.resourceRef]) {
                let resourceOption = codeToResourceOptionMap[propValueToInclude.resourceRef];
                extraInfo.resourceRefLabel = resourceOption.label;
                extraInfo.resourceTypeId = resourceOption.resourceTypeId;
            }

            if (codeToDateOptionsMap[propValueToInclude.dateRef]) {
                let dateOption = codeToDateOptionsMap[propValueToInclude.dateRef];
                extraInfo.dateRefLabel = dateOption.label;
            }

            let indirectInfoList = null;
            if (propValueToInclude.indirectRef) {
                indirectInfoList = [];

                let currentIndirectRef = propValueToInclude.indirectRef;
                while (currentIndirectRef) {
                    let indirectInfo: any = {};

                    indirectInfo.propDefId = currentIndirectRef.propDefId;

                    if (codeToDateOptionsMap[currentIndirectRef.dateRef]) {
                        let dateOption = codeToDateOptionsMap[currentIndirectRef.dateRef];
                        indirectInfo.dateRefLabel = dateOption.label;
                    }

                    if (currentIndirectRef.resourceRef !== "indirect") {
                        if (codeToResourceOptionMap[currentIndirectRef.resourceRef]) {
                            let resourceOption = codeToResourceOptionMap[currentIndirectRef.resourceRef];
                            indirectInfo.resourceRefLabel = resourceOption.label;
                            indirectInfo.resourceTypeId = resourceOption.resourceTypeId;
                        }
                    }

                    indirectInfoList.push(indirectInfo);

                    currentIndirectRef = currentIndirectRef.indirectRef;
                }
            }

            extraInfo.indirectInfoList = indirectInfoList;

            return extraInfo;
        }
    }


}